import React, { useState } from "react";
import { Link, useRouterState } from "@tanstack/react-router";
import {
  LayoutDashboard, Users, Megaphone, HandCoins, BarChart3,
  Settings, LogOut, Menu, X, FileText, Building2, ChevronDown, ChevronRight,
  Layers, ClipboardCheck, Shield, Star, MousePointer, Tag, FolderOpen,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { useAuth } from "@/lib/auth";
import type { LucideIcon } from "lucide-react";

interface NavItem {
  name: string;
  icon: LucideIcon;
  href?: string;
  children?: { name: string; href: string }[];
}

const navigation: NavItem[] = [
  { name: "Dashboard", icon: LayoutDashboard, href: "/" },
  {
    name: "Offers", icon: Megaphone,
    children: [
      { name: "All Offers", href: "/offers" },
      { name: "Offer Groups", href: "/offer-groups" },
      { name: "Applications", href: "/applications" },
    ],
  },
  {
    name: "Affiliates", icon: Users,
    children: [
      { name: "All Affiliates", href: "/affiliates" },
      { name: "Tiers", href: "/tiers" },
    ],
  },
  { name: "Advertisers", icon: HandCoins, href: "/advertisers" },
  { name: "Traffic Controls", icon: Shield, href: "/traffic-controls" },
  { name: "Smart Links", icon: MousePointer, href: "/smart-links" },
  {
    name: "Reporting", icon: BarChart3,
    children: [
      { name: "Overview", href: "/reporting" },
      { name: "Raw Reports", href: "/reporting/raw" },
      { name: "CSV Import", href: "/reporting/import" },
    ],
  },
  { name: "Invoices", icon: FileText, href: "/invoices" },
];

const adminNavigation: NavItem[] = [
  {
    name: "Settings", icon: Settings,
    children: [
      { name: "General", href: "/settings" },
      { name: "Categories", href: "/settings/categories" },
      { name: "Labels", href: "/settings/labels" },
      { name: "Users", href: "/settings/users" },
    ],
  },
];

interface DashboardLayoutProps {
  children: React.ReactNode;
}

function NetworkSwitcher() {
  const { user, networks, switchNetwork } = useAuth();
  const [open, setOpen] = useState(false);
  const [switching, setSwitching] = useState(false);

  // Filter out platform network and only show if multiple
  const visibleNetworks = networks.filter(
    (n) => n.networkName !== "__platform__" && n.userType !== "super_admin",
  );

  if (visibleNetworks.length <= 1) return null;

  const currentNetwork = visibleNetworks.find((n) => n.networkId === user?.networkId);

  async function handleSwitch(networkId: number) {
    if (networkId === user?.networkId) {
      setOpen(false);
      return;
    }
    setSwitching(true);
    try {
      await switchNetwork(networkId);
    } catch (err) {
      console.error(err);
    } finally {
      setSwitching(false);
      setOpen(false);
    }
  }

  return (
    <div className="relative px-4 py-2 border-t border-gray-200">
      <button
        onClick={() => setOpen(!open)}
        disabled={switching}
        className="w-full flex items-center gap-2 rounded-lg border border-gray-200 px-3 py-2 text-left hover:bg-gray-50 transition-colors"
      >
        <Building2 className="w-4 h-4 text-gray-400 shrink-0" />
        <span className="text-sm font-medium text-gray-700 truncate flex-1">
          {currentNetwork?.networkName || "Network"}
        </span>
        <ChevronDown className={cn("w-4 h-4 text-gray-400 shrink-0 transition-transform", open && "rotate-180")} />
      </button>

      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <div className="absolute left-4 right-4 bottom-full mb-1 z-50 bg-white rounded-lg shadow-lg border border-gray-200 py-1 max-h-48 overflow-y-auto">
            {visibleNetworks.map((net) => (
              <button
                key={net.networkId}
                onClick={() => handleSwitch(net.networkId)}
                className={cn(
                  "w-full flex items-center gap-2 px-3 py-2 text-sm text-left hover:bg-gray-50 transition-colors",
                  net.networkId === user?.networkId && "bg-gray-50 font-medium",
                )}
              >
                <Building2 className="w-4 h-4 text-gray-400 shrink-0" />
                <span className="truncate flex-1">{net.networkName}</span>
                <span className="text-xs text-gray-400 capitalize">{net.role}</span>
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

const exactMatchPaths = new Set(["/", "/settings"]);

function isPathActive(href: string, currentPath: string): boolean {
  if (exactMatchPaths.has(href)) return currentPath === href;
  return currentPath.startsWith(href);
}

function NavGroup({ item, currentPath, onNavigate }: { item: NavItem; currentPath: string; onNavigate: () => void }) {
  const isChildActive = item.children?.some((c) => isPathActive(c.href, currentPath)) ?? false;
  const [open, setOpen] = useState(isChildActive);

  if (!item.children) {
    const active = isPathActive(item.href!, currentPath);
    return (
      <Link
        to={item.href!}
        onClick={onNavigate}
        className={cn(
          "flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors",
          active ? "bg-gray-100 text-gray-900" : "text-gray-600 hover:bg-gray-50 hover:text-gray-900",
        )}
      >
        <item.icon className="w-[18px] h-[18px] shrink-0" />
        {item.name}
      </Link>
    );
  }

  return (
    <div>
      <button
        onClick={() => setOpen(!open)}
        className={cn(
          "w-full flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors",
          isChildActive ? "text-gray-900" : "text-gray-600 hover:bg-gray-50 hover:text-gray-900",
        )}
      >
        <item.icon className="w-[18px] h-[18px] shrink-0" />
        <span className="flex-1 text-left">{item.name}</span>
        <ChevronRight className={cn("w-4 h-4 text-gray-400 transition-transform duration-150", open && "rotate-90")} />
      </button>
      {open && (
        <div className="ml-4 pl-4 border-l border-gray-200 mt-0.5 mb-1 flex flex-col gap-0.5">
          {item.children.map((child) => {
            const active = isPathActive(child.href, currentPath);
            return (
              <Link
                key={child.href}
                to={child.href}
                onClick={onNavigate}
                className={cn(
                  "rounded-lg px-3 py-1.5 text-sm transition-colors",
                  active ? "bg-gray-100 text-gray-900 font-medium" : "text-gray-500 hover:bg-gray-50 hover:text-gray-900",
                )}
              >
                {child.name}
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
}

export function DashboardLayout({ children }: DashboardLayoutProps) {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const { user, logout } = useAuth();
  const routerState = useRouterState();
  const currentPath = routerState.location.pathname;

  return (
    <div className="flex h-screen overflow-hidden bg-gray-50">
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/50 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      <aside
        className={cn(
          "fixed inset-y-0 left-0 z-50 flex w-64 flex-col bg-white border-r border-gray-200 transition-transform duration-200 lg:relative lg:translate-x-0",
          sidebarOpen ? "translate-x-0" : "-translate-x-full",
        )}
      >
        <div className="flex items-center justify-between h-16 px-6 border-b border-gray-200 shrink-0">
          <Link to="/" className="text-xl font-bold tracking-tight">ClickDash</Link>
          <button className="lg:hidden" onClick={() => setSidebarOpen(false)}>
            <X className="w-5 h-5" />
          </button>
        </div>

        <nav className="flex flex-col gap-0.5 p-3 flex-1 overflow-y-auto">
          {navigation.map((item) => (
            <NavGroup key={item.name} item={item} currentPath={currentPath} onNavigate={() => setSidebarOpen(false)} />
          ))}

          {user?.role === "admin" && (
            <>
              <div className="mt-3 mb-1 px-3">
                <span className="text-[11px] font-semibold text-gray-400 uppercase tracking-wider">Admin</span>
              </div>
              {adminNavigation.map((item) => (
                <NavGroup key={item.name} item={item} currentPath={currentPath} onNavigate={() => setSidebarOpen(false)} />
              ))}
            </>
          )}
        </nav>

        <NetworkSwitcher />

        {user && (
          <div className="p-4 border-t border-gray-200 shrink-0">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center text-sm font-medium shrink-0">
                {user.firstName?.[0]}{user.lastName?.[0]}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium truncate">{user.firstName} {user.lastName}</p>
                <p className="text-xs text-gray-500 truncate">{user.email}</p>
              </div>
              <button onClick={logout} className="text-gray-400 hover:text-gray-600 shrink-0" title="Sign out">
                <LogOut className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}
      </aside>

      <div className="flex flex-1 flex-col min-w-0 overflow-hidden">
        <header className="flex items-center h-16 px-6 bg-white border-b border-gray-200 shrink-0">
          <button className="lg:hidden mr-4" onClick={() => setSidebarOpen(true)}>
            <Menu className="w-5 h-5" />
          </button>
          <div className="flex-1" />
        </header>

        <main className="flex-1 overflow-y-auto p-6">{children}</main>
      </div>
    </div>
  );
}
